import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import useAppStore from '../../store/appStore';
import { isAdminMaintenanceLockLive, SESSION_TTL_MS } from '../../utils/sessionLock';

type RadarFilter = 'all' | 'online' | 'inactive60' | 'acute90';
type PersonRole = 'Hauptadmin' | 'Admin' | 'Coach' | 'Support' | 'Mandant';

const HAUPTADMIN_EMAIL = 'erfolg@friseur-coaching.de';

const ROLE_RANK: Record<PersonRole, number> = {
    Hauptadmin: 5,
    Admin: 4,
    Support: 3,
    Coach: 2,
    Mandant: 1,
};

export interface AktivCounterModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Beim Öffnen gesetzter Filter (z. B. Radar → Akut) */
    initialFilter?: RadarFilter;
}

interface AktivPerson {
    id: string;
    name: string;
    email: string;
    role: PersonRole;
    location: string;
    lastActiveAt: unknown;
    snoozedUntil: unknown;
    kind: 'mandant' | 'team';
    /** Team: Aktivitätsimpulse heute */
    pingsToday?: number;
    /** Team: Session-Zähler (Pause > 6 Std. = neu) */
    sessions?: number;
}

/** Gleicher Puffer wie App-Heartbeat (2-Min-Takt → 20 Min Online). */
const MS_ONLINE = 20 * 60 * 1000;
const MS_60DAYS = 60 * 24 * 60 * 60 * 1000;
const MS_90DAYS = 90 * 24 * 60 * 60 * 1000;
const MS_4W = 28 * 24 * 60 * 60 * 1000;

const activityStatsFromUser = (u: any): { pingsToday: number; sessions: number } => {
    if (!u) return { pingsToday: 0, sessions: 0 };
    const todayKey = new Date().toISOString().slice(0, 10);
    const day = String(u.activityDay || '');
    return {
        pingsToday: day === todayKey ? Number(u.activityPingsToday) || 0 : 0,
        sessions: Number(u.activitySessionCount) || 0,
    };
};

const toMs = (raw: unknown): number | null => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw;
    if (typeof raw === 'string') {
        const parsed = Date.parse(raw);
        return Number.isNaN(parsed) ? null : parsed;
    }
    const anyRaw = raw as { seconds?: number; toDate?: () => Date };
    if (typeof anyRaw?.toDate === 'function') {
        const t = anyRaw.toDate().getTime();
        return Number.isNaN(t) ? null : t;
    }
    if (typeof anyRaw?.seconds === 'number') return anyRaw.seconds * 1000;
    return null;
};

const snoozeMs = (raw: unknown): number | null => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
        const parsed = Date.parse(raw);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return toMs(raw);
};

const isSnoozed = (raw: unknown): boolean => {
    const ts = snoozeMs(raw);
    return ts != null && ts > Date.now();
};

const resolveDisplayName = (doc: any, fallbackEmail = ''): string => {
    const fromParts = `${doc?.firstName || ''} ${doc?.lastName || ''}`.trim();
    return (
        String(doc?.displayName || '').trim() ||
        String(doc?.name || '').trim() ||
        fromParts ||
        String(doc?.email || fallbackEmail || '').trim() ||
        'Unbekannt'
    );
};

/** Wie AdminDashboard: Superadmin/Hauptadmin vs. Admin vs. Coach. */
const roleFromTeamMember = (m: any): PersonRole => {
    const cat = String(m?.category || '').toLowerCase();
    const role = String(m?.role || '').toLowerCase();
    const email = String(m?.email || '').trim().toLowerCase();
    const name = String(m?.name || '').trim();
    const isHauptadmin =
        email === HAUPTADMIN_EMAIL ||
        role === 'superadmin' ||
        cat === 'superadmin' ||
        name.includes('Christian');
    if (isHauptadmin) return 'Hauptadmin';
    if (cat === 'admin' || role === 'admin') return 'Admin';
    if (cat === 'support' || role === 'support' || role === 'supporter') return 'Support';
    return 'Coach';
};

const teamLocationForRole = (role: PersonRole, salonOrt?: string): string => {
    const loc = String(salonOrt || '').trim();
    if (loc) return loc;
    return role === 'Support' ? 'Support' : 'Team PreisExpert';
};

/** Team-Einträge mit gleicher Mail aber unterschiedlicher ID → eine Person. */
const preferTeamId = (a: string, b: string): string => {
    if (!a) return b;
    if (!b) return a;
    const aEmail = a.includes('@');
    const bEmail = b.includes('@');
    if (aEmail && !bEmail) return b;
    if (bEmail && !aEmail) return a;
    const aTeamGen = /^id_mgu/i.test(a);
    const bTeamGen = /^id_mgu/i.test(b);
    if (aTeamGen && !bTeamGen) return b;
    if (bTeamGen && !aTeamGen) return a;
    return a;
};

const upsertTeamPerson = (byKey: Map<string, AktivPerson>, person: AktivPerson) => {
    const em = person.email.trim().toLowerCase();
    const key = em ? `t:em:${em}` : `t:id:${person.id}`;
    const prev = byKey.get(key);
    if (!prev) {
        byKey.set(key, person);
        return;
    }
    const prevMs = toMs(prev.lastActiveAt);
    const nextMs = toMs(person.lastActiveAt);
    const betterActivity =
        nextMs != null && (prevMs == null || nextMs > prevMs) ? person.lastActiveAt : prev.lastActiveAt;
    const betterRole = ROLE_RANK[person.role] >= ROLE_RANK[prev.role] ? person.role : prev.role;
    byKey.set(key, {
        ...prev,
        ...person,
        id: preferTeamId(prev.id, person.id),
        name: prev.name !== 'Unbekannt' && prev.name !== 'Team' ? prev.name : person.name,
        email: prev.email || person.email,
        role: betterRole,
        location: prev.location || person.location,
        lastActiveAt: betterActivity,
        pingsToday: Math.max(Number(prev.pingsToday) || 0, Number(person.pingsToday) || 0),
        sessions: Math.max(Number(prev.sessions) || 0, Number(person.sessions) || 0),
        kind: 'team',
    });
};

const AktivCounterModal: React.FC<AktivCounterModalProps> = ({ isOpen, onClose, initialFilter = 'all' }) => {
    // Nur freigeschaltete PEOne-Operatoren (Rollen & Rechte / `team`) —
    // NICHT teamRegistry (globale Kontakte ohne Zugang, z. B. Bea Strango)
    const team = useAppStore((s) => s.team || []);

    const [people, setPeople] = useState<AktivPerson[]>([]);
    const [loading, setLoading] = useState(false);
    const [radarFilter, setRadarFilter] = useState<RadarFilter>(initialFilter);
    const [busyId, setBusyId] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) setRadarFilter(initialFilter);
    }, [isOpen, initialFilter]);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);

        (async () => {
            const byKey = new Map<string, AktivPerson>();
            const liveAkteLocks: Array<{
                holderUid: string;
                holderName: string;
                salonLabel: string;
                heartbeat: unknown;
            }> = [];

            try {
                const mandSnap = await getDocs(collection(db, 'kalkulation_mandanten'));

                mandSnap.docs.forEach((d) => {
                    const data = d.data() as any;
                    if (data.isArchived === true || data.isDeleted === true || data.isActive === false) return;
                    const cd = data.clientData || {};
                    // Vorabkunden nicht im AktivCounter auswerten
                    if (data.isLegacy === true || cd.isLegacy === true) return;
                    const name =
                        `${data.vorname || cd.vorname || ''} ${data.nachname || cd.nachname || ''}`.trim() ||
                        String(data.firmenname || cd.salonname || cd.firmenname || '').trim() ||
                        'Mandant';
                    const email = String(data.email || cd.mailadresse || cd.email || '').trim();
                    const location =
                        String(
                            data.firmenname ||
                                cd.salonname ||
                                cd.firmenname ||
                                data.ort ||
                                cd.ort ||
                                ''
                        ).trim() || '—';
                    byKey.set(`m:${d.id}`, {
                        id: d.id,
                        name,
                        email,
                        role: 'Mandant',
                        location,
                        lastActiveAt: data.lastActiveAt || cd.lastActiveAt || null,
                        snoozedUntil: data.snoozedUntil ?? cd.snoozedUntil ?? null,
                        kind: 'mandant',
                    });

                    // God-Mode / Wartungs-Lock ODER Team-Session → Holder als online in dieser Akte
                    const lockLive = isAdminMaintenanceLockLive({
                        isLockedByAdmin: data.isLockedByAdmin,
                        adminLockHeartbeat: data.adminLockHeartbeat,
                    });
                    const active = data.activeSession || null;
                    const activeTs =
                        typeof active?.timestamp === 'number'
                            ? active.timestamp
                            : typeof active?.timestamp?.toMillis === 'function'
                              ? active.timestamp.toMillis()
                              : 0;
                    const teamSessionLive =
                        !!active?.sessionId &&
                        !!active?.userId &&
                        active.holderIsTeam === true &&
                        !!activeTs &&
                        Date.now() - activeTs <= SESSION_TTL_MS;

                    if (lockLive || teamSessionLive) {
                        const holderUid = String(
                            data.adminLockHolderUid || (teamSessionLive ? active.userId : '') || ''
                        ).trim();
                        const holderName =
                            String(data.adminLockHolderName || '').trim() ||
                            String((teamSessionLive && active?.holderName) || '').trim() ||
                            (holderUid ? '' : 'PreisExpert-Team');
                        if (holderUid || holderName) {
                            liveAkteLocks.push({
                                holderUid,
                                holderName: holderName || 'PreisExpert-Team',
                                salonLabel: location !== '—' ? location : name,
                                heartbeat:
                                    data.adminLockHeartbeat ||
                                    activeTs ||
                                    Date.now(),
                            });
                        }
                    }
                });
            } catch {
                /* Rules / Offline */
            }

            const usersByEmail = new Map<string, any>();
            const usersById = new Map<string, any>();
            const newerActivity = (a: unknown, b: unknown): boolean => {
                const am = toMs(a);
                const bm = toMs(b);
                if (am == null) return false;
                if (bm == null) return true;
                return am > bm;
            };
            const pickBestActivity = (...vals: unknown[]): unknown => {
                let best: unknown = null;
                vals.forEach((v) => {
                    if (newerActivity(v, best)) best = v;
                });
                return best;
            };
            try {
                const userSnap = await getDocs(collection(db, 'users'));
                userSnap.docs.forEach((d) => {
                    const data = d.data() as any;
                    const row = { id: d.id, ...data };
                    usersById.set(d.id, row);
                    const em = String(data.email || '').trim().toLowerCase();
                    if (!em) return;
                    const prev = usersByEmail.get(em);
                    // Bei Doppel-Docs (alte ID vs Auth-UID): den mit neuerer Aktivität behalten
                    if (
                        !prev ||
                        newerActivity(data.lastActiveAt, prev.lastActiveAt) ||
                        newerActivity(data.lastAktePresence?.at, prev.lastAktePresence?.at)
                    ) {
                        usersByEmail.set(em, row);
                    }
                });
            } catch {
                /* Rules / Offline */
            }

            team.forEach((m: any) => {
                if (!m) return;
                const email = String(m.email || '').trim();
                const emKey = email.toLowerCase();
                const uById = m.id ? usersById.get(String(m.id).trim()) : null;
                const uByEmail = emKey ? usersByEmail.get(emKey) : null;
                const u = uById || uByEmail || null;
                const uid = String(u?.id || m.id || email || '').trim();
                if (!uid) return;
                const role = roleFromTeamMember(m);
                const salonOrt = String(
                    u?.salonname || u?.firmenname || u?.ort || u?.city || m.ort || ''
                ).trim();
                // Auth-UID-Doc + E-Mail-Doc zusammenführen (Jean-Bug: alte ID vs echte Auth-UID)
                const uAuth = usersById.get(uid);
                const bestActive = pickBestActivity(
                    uById?.lastActiveAt,
                    uById?.clientData?.lastActiveAt,
                    uById?.lastAktePresence?.at,
                    uByEmail?.lastActiveAt,
                    uByEmail?.clientData?.lastActiveAt,
                    uByEmail?.lastAktePresence?.at,
                    uAuth?.lastActiveAt,
                    uAuth?.lastAktePresence?.at
                );
                const stats = activityStatsFromUser(uAuth || uById || uByEmail);
                upsertTeamPerson(byKey, {
                    id: uid,
                    name: resolveDisplayName(
                        { displayName: u?.displayName, name: m.name || u?.name, firstName: u?.firstName, lastName: u?.lastName, email: u?.email || email },
                        email
                    ),
                    email: String(u?.email || email || '').trim(),
                    role,
                    location: teamLocationForRole(role, salonOrt),
                    lastActiveAt: bestActive,
                    snoozedUntil: null,
                    kind: 'team',
                    pingsToday: stats.pingsToday,
                    sessions: stats.sessions,
                });
            });

            // users-Docs NUR zur Anreicherung freigeschalteter Operatoren
            byKey.forEach((prev, key) => {
                if (prev.kind !== 'team') return;
                const uId = usersById.get(prev.id);
                const uEm = prev.email ? usersByEmail.get(prev.email.toLowerCase()) : null;
                const u = uId || uEm;
                if (!u) return;
                byKey.set(key, {
                    ...prev,
                    name:
                        prev.name === 'Unbekannt' || prev.name === 'Team'
                            ? resolveDisplayName(u, prev.email)
                            : prev.name,
                    email: prev.email || String(u.email || '').trim(),
                    lastActiveAt: pickBestActivity(
                        prev.lastActiveAt,
                        uId?.lastActiveAt,
                        uId?.clientData?.lastActiveAt,
                        uId?.lastAktePresence?.at,
                        uEm?.lastActiveAt,
                        uEm?.clientData?.lastActiveAt,
                        uEm?.lastAktePresence?.at
                    ),
                });
            });

            // Eingeloggten Operator nur anzeigen, wenn freigeschaltet (`team`)
            const me = auth.currentUser;
            if (me?.uid) {
                const em = String(me.email || '').trim().toLowerCase();
                const fromTeam =
                    team.find((m: any) => String(m?.id || '').trim() === me.uid) ||
                    team.find((m: any) => String(m?.email || '').trim().toLowerCase() === em) ||
                    null;
                if (fromTeam) {
                    const u = usersById.get(me.uid) || (em && usersByEmail.get(em)) || null;
                    const role = roleFromTeamMember({ ...fromTeam, email: fromTeam.email || me.email });
                    const email = String(u?.email || me.email || fromTeam?.email || '').trim();
                    const meStats = activityStatsFromUser(u);
                    upsertTeamPerson(byKey, {
                        id: me.uid,
                        name: resolveDisplayName(
                            {
                                displayName: me.displayName || u?.displayName,
                                name: fromTeam?.name || u?.name,
                                firstName: u?.firstName,
                                lastName: u?.lastName,
                                email,
                            },
                            email
                        ),
                        email,
                        role,
                        location: teamLocationForRole(
                            role,
                            String(u?.salonname || u?.ort || u?.city || '').trim()
                        ),
                        lastActiveAt: u?.lastActiveAt || u?.clientData?.lastActiveAt || null,
                        snoozedUntil: null,
                        kind: 'team',
                        pingsToday: meStats.pingsToday,
                        sessions: meStats.sessions,
                    });
                }
            }

            // Team-Mitglieder, die gerade per God-Mode eine Akte sperren → online + Ort = Salon
            liveAkteLocks.forEach((lock) => {
                const holderUid = lock.holderUid;
                let holderName = lock.holderName;
                const holderUser = holderUid ? usersById.get(holderUid) : null;
                const holderEmail = String(holderUser?.email || '').trim().toLowerCase();
                if ((!holderName || holderName === 'PreisExpert-Team') && holderUser) {
                    holderName = resolveDisplayName(holderUser, holderEmail);
                }
                if ((!holderName || holderName === 'PreisExpert-Team') && holderUid) {
                    const fromTeam =
                        team.find((m: any) => String(m?.id || '').trim() === holderUid) ||
                        (holderEmail
                            ? team.find((m: any) => String(m?.email || '').trim().toLowerCase() === holderEmail)
                            : null);
                    if (fromTeam?.name) holderName = String(fromTeam.name).trim();
                }
                const holderNameLower = holderName.toLowerCase();
                let foundKey: string | null = null;
                let found: AktivPerson | null = null;
                for (const [key, person] of byKey.entries()) {
                    if (person.kind !== 'team') continue;
                    const personEmail = person.email.trim().toLowerCase();
                    const nameMatch =
                        person.name.toLowerCase() === holderNameLower ||
                        (holderNameLower.length > 2 && person.name.toLowerCase().includes(holderNameLower)) ||
                        (person.name.toLowerCase().length > 2 && holderNameLower.includes(person.name.toLowerCase()));
                    if (
                        (holderUid && person.id === holderUid) ||
                        (holderEmail && personEmail === holderEmail) ||
                        nameMatch
                    ) {
                        foundKey = key;
                        found = person;
                        break;
                    }
                }
                if (found && foundKey) {
                    byKey.set(foundKey, {
                        ...found,
                        id: holderUid || found.id,
                        name:
                            found.name !== 'Unbekannt' && found.name !== 'Team'
                                ? found.name
                                : holderName,
                        email: found.email || holderEmail,
                        location: `In Akte: ${lock.salonLabel}`,
                        lastActiveAt: lock.heartbeat || Date.now(),
                    });
                    return;
                }
                const id = holderUid || `lock:${holderName}`;
                byKey.set(`t:id:${id}`, {
                    id,
                    name: holderName,
                    email: holderEmail,
                    role: 'Coach',
                    location: `In Akte: ${lock.salonLabel}`,
                    lastActiveAt: lock.heartbeat || Date.now(),
                    snoozedUntil: null,
                    kind: 'team',
                });
            });

            // Presence + beste lastActiveAt / Stats nachziehen (Auth-UID-Doc schlägt alte IDs)
            byKey.forEach((person, key) => {
                if (person.kind !== 'team') return;
                const uId = usersById.get(person.id);
                const uEm = person.email ? usersByEmail.get(person.email.toLowerCase()) : null;
                const stats = activityStatsFromUser(uId || uEm);
                const presence = uId?.lastAktePresence || uEm?.lastAktePresence;
                const bestActive = pickBestActivity(
                    person.lastActiveAt,
                    uId?.lastActiveAt,
                    uId?.lastAktePresence?.at,
                    uEm?.lastActiveAt,
                    uEm?.lastAktePresence?.at
                );
                const at = toMs(presence?.at);
                const inAkteNow = String(person.location || '').startsWith('In Akte:');
                const withStats = {
                    ...person,
                    pingsToday: Math.max(Number(person.pingsToday) || 0, stats.pingsToday),
                    sessions: Math.max(Number(person.sessions) || 0, stats.sessions),
                };
                if (inAkteNow) {
                    byKey.set(key, { ...withStats, lastActiveAt: bestActive || Date.now() });
                    return;
                }
                if (at != null && Date.now() - at <= SESSION_TTL_MS) {
                    const salon = String(presence?.mandantUid || '').trim();
                    const mand = salon ? byKey.get(`m:${salon}`) : null;
                    const salonLabel =
                        mand?.location && mand.location !== '—' ? mand.location : mand?.name || 'Akte';
                    byKey.set(key, {
                        ...withStats,
                        location: `In Akte: ${salonLabel}`,
                        lastActiveAt: bestActive || presence.at || Date.now(),
                    });
                    return;
                }
                if (bestActive && bestActive !== person.lastActiveAt) {
                    byKey.set(key, { ...withStats, lastActiveAt: bestActive });
                    return;
                }
                byKey.set(key, withStats);
            });

            const list = Array.from(byKey.values()).sort((a, b) => {
                // Team vor Mandanten, Online zuerst
                if (a.kind !== b.kind) return a.kind === 'team' ? -1 : 1;
                const am = toMs(a.lastActiveAt);
                const bm = toMs(b.lastActiveAt);
                const ao = am != null && Date.now() - am <= MS_ONLINE ? 0 : 1;
                const bo = bm != null && Date.now() - bm <= MS_ONLINE ? 0 : 1;
                if (ao !== bo) return ao - bo;
                if (am == null && bm == null) return a.name.localeCompare(b.name, 'de');
                if (am == null) return 1;
                if (bm == null) return -1;
                return bm - am;
            });

            if (!cancelled) {
                setPeople(list);
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, team]);

    const filtered = useMemo(() => {
        return people.filter((p) => {
            if (radarFilter === 'all') return true;
            const inactiveMs = (() => {
                const ts = toMs(p.lastActiveAt);
                return ts == null ? null : Date.now() - ts;
            })();
            if (radarFilter === 'online') {
                if (String(p.location || '').startsWith('In Akte:')) return true;
                return inactiveMs != null && inactiveMs <= MS_ONLINE;
            }
            // Nur mit erfasster Aktivität — „nichts erfasst“ zählt nicht als inaktiv
            if (radarFilter === 'inactive60') {
                if (p.kind === 'mandant' && inactiveMs == null) return false;
                return inactiveMs != null && inactiveMs >= MS_60DAYS;
            }
            if (radarFilter === 'acute90') {
                if (isSnoozed(p.snoozedUntil)) return false;
                if (p.kind === 'mandant' && inactiveMs == null) return false;
                return inactiveMs != null && inactiveMs >= MS_90DAYS;
            }
            return true;
        });
    }, [people, radarFilter]);

    const handleSnooze = async (person: AktivPerson) => {
        if (person.kind !== 'mandant') return;
        setBusyId(person.id);
        const until = Date.now() + MS_4W;
        try {
            await updateDoc(doc(db, 'kalkulation_mandanten', person.id), { snoozedUntil: until });
            setPeople((prev) => prev.map((x) => (x.id === person.id ? { ...x, snoozedUntil: until } : x)));
        } catch {
            alert('Snooze fehlgeschlagen.');
        } finally {
            setBusyId(null);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in text-left">
            <div className="bg-[#0f1015] border border-slate-800 rounded-2xl w-full max-w-3xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col max-h-[85vh]">
                <div className="p-6 border-b border-slate-800 shrink-0 bg-[#15171e] rounded-t-2xl">
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <div className="text-[10px] text-[#E6C299] font-black tracking-[0.3em] uppercase mb-1">
                                Live-Status · Team & Mandanten
                            </div>
                            <h2 className="text-xl font-bold text-white tracking-widest uppercase">AktivCounter</h2>
                            <p className="text-[10px] text-slate-500 mt-1">
                                Team: Online, Pings heute, Sessions · Mandanten: Inaktivität
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-400 transition-colors"
                            aria-label="Schließen"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                        {(
                            [
                                ['all', 'Alle'],
                                ['online', '🟢 Online'],
                                ['inactive60', '⏳ Inaktiv (> 60T)'],
                                ['acute90', '🔴 Akut (> 90T)'],
                            ] as const
                        ).map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setRadarFilter(key)}
                                className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all ${
                                    radarFilter === key
                                        ? key === 'acute90'
                                            ? 'bg-red-500/15 border-red-500/60 text-red-400'
                                            : key === 'inactive60'
                                              ? 'bg-amber-500/15 border-amber-500/60 text-amber-400'
                                              : key === 'online'
                                                ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-400'
                                                : 'bg-[#E6C299]/10 border-[#E6C299] text-[#E6C299]'
                                        : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:text-white'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-2">
                    {loading ? (
                        <div className="p-8 text-center text-slate-500 animate-pulse font-bold uppercase tracking-widest text-xs">
                            Lade Aktivitätsdaten…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-8 text-center text-slate-500 text-sm">Keine Einträge in diesem Filter.</div>
                    ) : (
                        filtered.map((p) => {
                            const inactiveMs = (() => {
                                const ts = toMs(p.lastActiveAt);
                                return ts == null ? null : Date.now() - ts;
                            })();
                            const snoozed = isSnoozed(p.snoozedUntil);
                            const untilTs = snoozeMs(p.snoozedUntil);
                            const untilStr = untilTs ? new Date(untilTs).toLocaleDateString('de-DE') : '';
                            const acute = inactiveMs != null && inactiveMs >= MS_90DAYS;
                            const warn = inactiveMs != null && inactiveMs >= MS_60DAYS;

                            let statusNode: React.ReactNode;
                            const inAkte = String(p.location || '').startsWith('In Akte:');
                            if (snoozed) {
                                statusNode = (
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-blue-400">
                                        <span className="inline-block w-2 h-2 rounded-full bg-blue-400/70" />
                                        Pausiert bis {untilStr}
                                    </span>
                                );
                            } else if (inAkte) {
                                statusNode = (
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-400">
                                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                        Gerade online · {p.location}
                                    </span>
                                );
                            } else if (inactiveMs == null) {
                                statusNode = (
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                                        <span className="inline-block w-2 h-2 rounded-full bg-slate-600" />
                                        Noch keine Aktivität erfasst
                                    </span>
                                );
                            } else if (inactiveMs <= MS_ONLINE) {
                                statusNode = (
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-400">
                                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                        Gerade online
                                    </span>
                                );
                            } else {
                                const HOUR = 60 * 60 * 1000;
                                const DAY = 24 * HOUR;
                                let label: string;
                                if (inactiveMs < HOUR) label = `Vor ${Math.floor(inactiveMs / 60000)} Min aktiv`;
                                else if (inactiveMs < DAY) label = `Vor ${Math.floor(inactiveMs / HOUR)} Std aktiv`;
                                else label = `Vor ${Math.floor(inactiveMs / DAY)} Tagen aktiv`;
                                const color = acute ? 'text-red-400' : warn ? 'text-amber-400' : 'text-slate-400';
                                const dot = acute ? 'bg-red-400' : warn ? 'bg-amber-400' : 'bg-slate-500';
                                statusNode = (
                                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold ${color}`}>
                                        <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
                                        {label}
                                    </span>
                                );
                            }

                            const badgeClass =
                                p.role === 'Hauptadmin'
                                    ? 'bg-red-500/15 text-red-400 border-red-500/50'
                                    : p.role === 'Admin'
                                    ? 'bg-red-500/10 text-red-400 border-red-500/30'
                                    : p.role === 'Support'
                                      ? 'bg-sky-500/10 text-sky-400 border-sky-500/30'
                                      : p.role === 'Coach'
                                        ? 'bg-[#E6C299]/10 text-[#E6C299] border-[#E6C299]/30'
                                        : 'bg-slate-700/40 text-slate-300 border-slate-600/40';

                            return (
                                <div
                                    key={`${p.kind}-${p.email || p.id}`}
                                    className="rounded-xl border border-slate-800 bg-[#15171e] px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                                >
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-bold text-white text-sm">{p.name}</span>
                                            <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${badgeClass}`}>
                                                {p.role}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-1 truncate">
                                            {p.email ? `${p.email} · ${p.location}` : p.location}
                                        </div>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                                            {statusNode}
                                            {p.kind === 'team' && (
                                                <span className="text-[10px] font-mono text-sky-400/90">
                                                    {Number(p.pingsToday) || 0} Pings · {Number(p.sessions) || 0} Sessions
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {p.kind === 'mandant' && acute && !snoozed && (
                                        <button
                                            type="button"
                                            disabled={busyId === p.id}
                                            onClick={() => void handleSnooze(p)}
                                            className="px-2.5 py-1.5 rounded border border-slate-700 bg-slate-800 text-[9px] font-black uppercase tracking-widest text-slate-300 hover:text-white hover:border-blue-500/50 disabled:opacity-40"
                                        >
                                            4W Pause
                                        </button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AktivCounterModal;
